import WindChime, { type WindChimeOptions, type WindChimeState } from '../wind-chime/wind-chime.mjs';
import { windChimeStrings } from './wind-chime-strings';

export const homepageWindChimeOptions = Object.freeze({
    volume: 0.02,
    muted: true,
    baseFrequency: 440,
    scale: [0, 3, 5, 7, 10, 12, 15, 17],
    wind: 0.60,
    gustiness: 0.60,
    brightness: 0.28,
    reverb: 1.00,
    decay: 8
} satisfies WindChimeOptions);

export const homepageWindChimeMaximumVolume = 0.30;

/** Converts the homepage slider percentage to the capped library gain. */
export function scaleHomepageWindChimeVolume(sliderPercentage: number): number {
    const boundedPercentage = Math.min(100, Math.max(0, sliderPercentage));
    return boundedPercentage / 100 * homepageWindChimeMaximumVolume;
}

export type WindChimeControlPresentation = {
    muted: boolean;
    label: string;
    status: string;
};

/** Maps the audio engine's actual lifecycle state to the visible control state. */
export function getWindChimeControlPresentation(
    state: WindChimeState,
    muted: boolean
): WindChimeControlPresentation {
    if (state === 'running' && !muted) {
        return { muted: false, label: windChimeStrings.mute, status: windChimeStrings.playingStatus };
    }

    if (state === 'starting' && !muted) {
        return { muted: true, label: windChimeStrings.mute, status: windChimeStrings.startingStatus };
    }

    if (state === 'paused' && !muted) {
        return { muted: true, label: windChimeStrings.resume, status: windChimeStrings.pausedStatus };
    }

    const label = state === 'running' ? windChimeStrings.unmute : windChimeStrings.start;
    return { muted: true, label, status: windChimeStrings.mutedStatus };
}

function requireElement<T extends Element>(id: string, constructor: { new(...args: never[]): T }): T {
    const element = document.getElementById(id);
    if (!(element instanceof constructor)) throw new Error(`Required page element '${id}' was not found.`);
    return element;
}

/** Initializes the homepage wind-chime controls and returns their teardown callback. */
export function initializeWindChimeControls(): () => void {
    const container = requireElement('wind-chime-control', HTMLElement);
    const toggle = requireElement('wind-chime-toggle', HTMLButtonElement);
    const volume = requireElement('wind-chime-volume', HTMLInputElement);
    const volumeLabel = requireElement('wind-chime-volume-label', HTMLElement);
    const status = requireElement('wind-chime-status', HTMLElement);
    const chimes = new WindChime(homepageWindChimeOptions);
    const longPressMilliseconds = 450;
    const expandedDurationMilliseconds = 2_000;
    let longPressTimeoutId = 0;
    let collapseTimeoutId = 0;
    let suppressNextClick = false;
    let playbackError = false;
    let muted = chimes.muted;
    let playbackIntent = 0;

    const render = (): void => {
        const presentation = getWindChimeControlPresentation(chimes.state, muted);
        toggle.dataset.muted = String(presentation.muted);
        toggle.setAttribute('aria-label', presentation.label);
        toggle.setAttribute('aria-pressed', String(presentation.muted));
        toggle.title = presentation.label;
        status.textContent = playbackError ? windChimeStrings.errorStatus : presentation.status;
    };

    const reportPlaybackError = (error: unknown): void => {
        if (!playbackError) console.error('Wind chime playback failed.', error);
        playbackError = true;
        render();
    };

    const clearLongPress = (): void => {
        if (longPressTimeoutId === 0) return;
        window.clearTimeout(longPressTimeoutId);
        longPressTimeoutId = 0;
    };

    const collapse = (): void => {
        container.removeAttribute('data-expanded');
        collapseTimeoutId = 0;
    };

    const keepExpandedTemporarily = (): void => {
        container.dataset.expanded = 'true';
        if (collapseTimeoutId !== 0) window.clearTimeout(collapseTimeoutId);
        collapseTimeoutId = window.setTimeout(collapse, expandedDurationMilliseconds);
    };

    const handleToggleClick = (): void => {
        if (suppressNextClick) {
            suppressNextClick = false;
            return;
        }

        playbackError = false;
        keepExpandedTemporarily();
        const intent = ++playbackIntent;
        if (!muted && (chimes.state === 'running' || chimes.state === 'starting')) {
            muted = true;
            chimes.stopGracefully();
            render();
            return;
        }

        muted = false;
        chimes.muted = false;
        render();
        try {
            void chimes.start().catch(error => {
                if (intent !== playbackIntent || muted) return;
                muted = true;
                reportPlaybackError(error);
            });
        } catch (error) {
            if (intent !== playbackIntent || muted) return;
            muted = true;
            reportPlaybackError(error);
        }
    };

    const handlePointerDown = (event: PointerEvent): void => {
        if (event.pointerType === 'mouse') return;
        clearLongPress();
        longPressTimeoutId = window.setTimeout(() => {
            longPressTimeoutId = 0;
            suppressNextClick = true;
            keepExpandedTemporarily();
        }, longPressMilliseconds);
    };

    const handlePointerRelease = (): void => {
        clearLongPress();
        if (!suppressNextClick) return;
        volume.focus({ preventScroll: true });
    };

    const handlePointerCancel = (): void => {
        clearLongPress();
        suppressNextClick = false;
    };

    const handleVolumeInput = (): void => {
        chimes.volume = scaleHomepageWindChimeVolume(Number(volume.value));
        volume.setAttribute('aria-valuetext', windChimeStrings.volumeLevel(Math.round(chimes.volume * 100)));
        keepExpandedTemporarily();
    };

    const unsubscribeState = chimes.on('statechange', ({ state }) => {
        if (state === 'running') playbackError = false;
        render();
    });
    const unsubscribeError = chimes.on('error', error => {
        reportPlaybackError(error);
    });

    toggle.addEventListener('click', handleToggleClick);
    toggle.addEventListener('pointerdown', handlePointerDown);
    toggle.addEventListener('pointerup', handlePointerRelease);
    toggle.addEventListener('pointercancel', handlePointerCancel);
    toggle.addEventListener('pointerleave', clearLongPress);
    volume.addEventListener('input', handleVolumeInput);
    volumeLabel.textContent = windChimeStrings.volume;
    volume.value = String(homepageWindChimeOptions.volume / homepageWindChimeMaximumVolume * 100);
    volume.setAttribute('aria-valuetext', windChimeStrings.volumeLevel(Math.round(homepageWindChimeOptions.volume * 100)));
    render();
    container.hidden = false;

    let cleanedUp = false;
    function handlePageHide(event: PageTransitionEvent): void {
        if (!event.persisted) cleanup();
    }

    function cleanup(): void {
        if (cleanedUp) return;
        cleanedUp = true;
        ++playbackIntent;
        clearLongPress();
        if (collapseTimeoutId !== 0) {
            window.clearTimeout(collapseTimeoutId);
            collapseTimeoutId = 0;
        }
        window.removeEventListener('pagehide', handlePageHide);
        toggle.removeEventListener('click', handleToggleClick);
        toggle.removeEventListener('pointerdown', handlePointerDown);
        toggle.removeEventListener('pointerup', handlePointerRelease);
        toggle.removeEventListener('pointercancel', handlePointerCancel);
        toggle.removeEventListener('pointerleave', clearLongPress);
        volume.removeEventListener('input', handleVolumeInput);
        unsubscribeState();
        unsubscribeError();
        void chimes.destroy();
    }

    window.addEventListener('pagehide', handlePageHide);

    return cleanup;
}
